import { ActionIcon, Button, Group, Tooltip } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { SvgThumbDown, SvgThumbDownFilled, SvgThumbUp, SvgThumbUpFilled } from '@mygpt/app/chat/components/icon';
import { useMedia } from '@mygpt/app/utils';
import { useTranslation } from 'react-i18next';
import { useFeedbackModal } from '@mygpt/app/chat/hooks/useFeedbackModal';
import { ratingType } from '@mygpt/types';
import { useMatch } from 'react-router-dom';

interface LikeButtonProps {
  aiId: string;
  sessionId: string;
  messageId: string;
  rating?: ratingType;
}

export const LikeButton: React.FC<LikeButtonProps> = ({ aiId, sessionId, messageId, rating }) => {
  const { isMobile } = useMedia();
  const { t } = useTranslation();
  const { openModal, setCurrentMessageInfo } = useFeedbackModal();
  const [active, setActive] = useState<boolean>(false);
  const match = useMatch('/widget/:botId');
  const isWidgetPage = !!match;
  const hasRating = [2, -1].includes(rating || 0);

  useEffect(() => {
    if (rating) {
      setActive(rating === 2);
    }
  }, [rating]);

  if (isMobile && !isWidgetPage) {
    return (
      <div className="flex flex-col items-center flex-1">
        <Button
          disabled={hasRating}
          variant={'default'}
          color="indigo"
          onClick={() => {
            openModal('good');
            setCurrentMessageInfo({ aiId, sessionId, messageId, rating });
          }}
          className="border-[#E4E7EC] rounded-md h-10 px-2 w-full flex items-center justify-center gap-1"
        >
          {active ? (
            <SvgThumbUpFilled />
          ) : (
            <span className="hover:text-gray-600 text-gray-500">
              <SvgThumbUp />
            </span>
          )}
          {isMobile && <span className="ml-1 text-xs text-gray-600">{t('completions.rate.like')}</span>}
        </Button>
      </div>
    );
  }

  return (
    <Group gap={5} wrap="nowrap">
      <Tooltip label={<Trans i18nKey={'completions.thumb_up'} />} withArrow multiline w={240}>
        <ActionIcon
          disabled={rating !== undefined}
          variant={active ? 'transparent' : 'subtle'}
          color="indigo"
          onClick={() => {
            openModal('good');
            setCurrentMessageInfo({ aiId, sessionId, messageId, rating });
          }}
        >
          {active ? (
            <SvgThumbUpFilled />
          ) : (
            <span className="hover:text-gray-600 text-gray-500">
              <SvgThumbUp />
            </span>
          )}
        </ActionIcon>
      </Tooltip>
    </Group>
  );
};

export const DislikeButton: React.FC<LikeButtonProps> = ({ aiId, sessionId, messageId, rating }) => {
  const { isMobile } = useMedia();
  const { t } = useTranslation();
  const { openModal, setCurrentMessageInfo } = useFeedbackModal();
  const [active, setActive] = useState<boolean>(false);
  const match = useMatch('/widget/:botId');
  const isWidgetPage = !!match;
  const hasRating = [2, -1].includes(rating || 0);

  useEffect(() => {
    if (rating) {
      setActive(rating !== 2);
    }
  }, [rating]);

  if (isMobile && !isWidgetPage) {
    return (
      <div className="flex flex-col items-center flex-1">
        <Button
          disabled={hasRating}
          variant={'default'}
          color="indigo"
          onClick={() => {
            openModal('bad');
            setCurrentMessageInfo({ aiId, sessionId, messageId, rating });
          }}
          className="border-[#E4E7EC] rounded-md h-10 px-2 w-full flex items-center justify-center gap-1"
        >
          {active ? (
            <SvgThumbDownFilled />
          ) : (
            <span className="hover:text-gray-600 text-gray-500">
              <SvgThumbDown />
            </span>
          )}
          {isMobile && <span className="ml-1 text-xs text-gray-600">{t('completions.rate.dislike')}</span>}
        </Button>
      </div>
    );
  }

  return (
    <Group gap={5} wrap="nowrap">
      <Tooltip label={<Trans i18nKey={'completions.thumb_down'} />} withArrow multiline w={240}>
        <ActionIcon
          disabled={rating !== undefined}
          variant={active ? 'transparent' : 'subtle'}
          color="indigo"
          onClick={() => {
            openModal('bad');
            setCurrentMessageInfo({ aiId, sessionId, messageId, rating });
          }}
        >
          {active ? (
            <SvgThumbDownFilled />
          ) : (
            <span className="hover:text-gray-600 text-gray-500">
              <SvgThumbDown />
            </span>
          )}
        </ActionIcon>
      </Tooltip>
    </Group>
  );
};
